import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FlaskConical,
  Search,
  Plus,
  Trash2,
  Upload,
  Play,
  PlayCircle,
  RotateCcw,
  Download,
  FileText,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  FileJson,
  AlertTriangle,
  FileDown,
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  Eye,
  EyeOff,
  Settings2,
  Square,
  Info,
  List,
  LayoutGrid,
} from 'lucide-react'
import { apiClient, LarsRun, LarsJob, LarsStageStatus, LarsReportSummary, LarsRunsListResponse } from '../../lib/api'
import ConfirmationModal from '../../components/ConfirmationModal'
import LarsConfigEditor from '../../components/LarsConfigEditor'

// ─── Constants ────────────────────────────────────────────────────────────────

const STAGE_NAMES: Record<number, string> = {
  1: 'Data Preparation',
  2: 'Query Execution',
  3: 'Scoring Engine',
  4: 'Reporting',
}

const STAGE_DESCRIPTIONS: Record<number, string> = {
  1: 'Parses and normalises SDL Q&A data for analysis',
  2: 'Sends prepared Q&A to AI models for responses',
  3: 'Grades each AI response against SDL answer criteria',
  4: 'Generates the executive PDF report',
}

const POLL_INTERVAL_MS = 3000
const LARS_STALE_MS = 5 * 60 * 1000
const LARS_RUNS_CACHE_KEY = 'lars_runs_cache'

function readLarsRunsCache(): LarsRunsListResponse | undefined {
  try {
    const raw = sessionStorage.getItem(LARS_RUNS_CACHE_KEY)
    if (!raw) return undefined
    return JSON.parse(raw) as LarsRunsListResponse
  } catch {
    return undefined
  }
}

function writeLarsRunsCache(data: LarsRunsListResponse) {
  try {
    sessionStorage.setItem(LARS_RUNS_CACHE_KEY, JSON.stringify(data))
  } catch {
    // sessionStorage may be unavailable
  }
}

function clearLarsRunsCache() {
  try {
    sessionStorage.removeItem(LARS_RUNS_CACHE_KEY)
  } catch {
    // ignore
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface ActiveJob {
  jobId: string
  stageNumber: number
}

interface StageResultCache {
  [stageNumber: number]: Record<string, any> | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelativeTime(iso: string | null): string {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function formatAbsoluteTime(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

function downloadJson(data: Record<string, any>, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function RunProgressBar({ completed, total }: { completed: number; total: number }) {
  const pct = total > 0 ? (completed / total) * 100 : 0
  const colour =
    completed === total ? 'bg-emerald-500' : completed > 0 ? 'bg-violet-500' : 'bg-gray-200'
  return (
    <div className="mt-1 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${colour}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

function StageBadge({ status }: { status: 'not_started' | 'running' | 'completed' | 'failed' | 'cancelled' }) {
  const map = {
    not_started: { label: 'Not started', cls: 'bg-gray-100 text-gray-500', icon: Clock },
    running: { label: 'Running…', cls: 'bg-violet-100 text-violet-700', icon: Loader2 },
    completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    failed: { label: 'Failed', cls: 'bg-red-100 text-red-600', icon: XCircle },
    cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-500', icon: Square },
  }
  const { label, cls, icon: Icon } = map[status]
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      {label}
    </span>
  )
}

// ─── Stage Card ───────────────────────────────────────────────────────────────

interface StageCardProps {
  stageNumber: number
  stageStatus: LarsStageStatus | undefined
  activeJob: ActiveJob | null
  result: Record<string, any> | null
  runName: string
  onRun: (stage: number) => void
  onResume?: (stage: number) => void
  onRetryFailed?: (stage: number) => void
  onCancel?: (stage: number) => void
  onDownloadJson: (stage: number) => void
  onDownloadLog: (stage: number) => void
  runLoading: boolean
  resumeLoading?: boolean
  retryFailedLoading?: boolean
  cancelLoading?: boolean
  disableRun?: boolean
  disableRunTitle?: string
}

function StageCard({
  stageNumber,
  stageStatus,
  activeJob,
  result,
  runName: _runName,
  onRun,
  onResume,
  onRetryFailed,
  onCancel,
  onDownloadJson,
  onDownloadLog,
  runLoading,
  resumeLoading = false,
  retryFailedLoading = false,
  cancelLoading = false,
  disableRun = false,
  disableRunTitle,
}: StageCardProps) {
  const [expanded, setExpanded] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!dropdownOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [dropdownOpen])

  const isRunning = activeJob?.stageNumber === stageNumber
  const isFailed = !isRunning && !stageStatus?.completed && !!result && 'error' in result
  const isCompleted = stageStatus?.completed ?? false

  const stageJobStatus: 'not_started' | 'running' | 'completed' | 'failed' = isRunning
    ? 'running'
    : isCompleted
      ? 'completed'
      : isFailed
        ? 'failed'
        : 'not_started'

  const prevCompleted = useRef(isCompleted)
  useEffect(() => {
    if (!prevCompleted.current && isCompleted) setExpanded(true)
    prevCompleted.current = isCompleted
  }, [isCompleted])

  const previewKeys = result ? Object.keys(result).slice(0, 6) : []

  return (
    <div
      className={`rounded-xl border transition-all duration-200 ${isRunning
        ? 'border-violet-300 bg-violet-50/40 shadow-sm'
        : isCompleted
          ? 'border-emerald-200 bg-white'
          : 'border-gray-200 bg-white'
        }`}
    >
      <div className="flex items-center gap-3 px-5 py-4">
        <div
          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold ${isCompleted
            ? 'bg-emerald-500 text-white'
            : isRunning
              ? 'bg-violet-500 text-white'
              : 'bg-gray-100 text-gray-500'
            }`}
        >
          {stageNumber}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">{STAGE_NAMES[stageNumber]}</p>
          <p className="text-xs text-gray-500 truncate">{STAGE_DESCRIPTIONS[stageNumber]}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StageBadge status={stageJobStatus} />
          {isCompleted && (
            <button
              onClick={() => setExpanded((p) => !p)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title={expanded ? 'Collapse' : 'Expand result'}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>

      {isRunning && (
        <div className="px-5 pb-3">
          <div className="h-1.5 rounded-full bg-violet-100 overflow-hidden">
            <div className="h-full w-full rounded-full bg-violet-400 animate-pulse" />
          </div>
          <p className="mt-1.5 text-xs text-violet-600">Processing — polling for result…</p>
        </div>
      )}

      {isCompleted && stageStatus?.last_updated && !isRunning && (
        <div className="px-5 pb-3 -mt-1 flex items-center gap-1.5">
          <Clock className="h-3 w-3 text-gray-300 flex-shrink-0" />
          <p className="text-xs text-gray-400">
            Last run: <span className="text-gray-500 font-medium">{formatAbsoluteTime(stageStatus.last_updated)}</span>
            <span className="ml-1 text-gray-400">({formatRelativeTime(stageStatus.last_updated)})</span>
          </p>
        </div>
      )}

      {expanded && isCompleted && (
        <div className="border-t border-gray-100 px-5 py-4 space-y-3">
          {result ? (
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs font-mono text-gray-700 overflow-auto max-h-48">
              {previewKeys.map((k) => (
                <div key={k} className="flex gap-2">
                  <span className="text-violet-600 flex-shrink-0">"{k}":</span>
                  <span className="text-gray-600 truncate">
                    {typeof result[k] === 'object'
                      ? Array.isArray(result[k])
                        ? `[${result[k].length} items]`
                        : '{…}'
                      : JSON.stringify(result[k])}
                  </span>
                </div>
              ))}
              {Object.keys(result).length > 6 && (
                <p className="text-gray-400 mt-1">…and {Object.keys(result).length - 6} more fields</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">Result data not yet loaded.</p>
          )}
        </div>
      )}

      {/* ── Action row: single flex line, no wrapping ──────────────────────── */}
      <div className="flex items-center gap-2 px-5 py-3 border-t border-gray-100">

        {/* Run / Re-run — split button when onResume/onRetryFailed exist */}
        <div className="relative flex" ref={dropdownRef}>
          {/* Main action button */}
          <button
            onClick={() => onRun(stageNumber)}
            disabled={isRunning || runLoading || resumeLoading || retryFailedLoading || disableRun}
            title={disableRun ? disableRunTitle : undefined}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${(onResume || onRetryFailed)
              ? 'rounded-l-lg border-r'
              : 'rounded-lg'
              } ${isCompleted
                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed border-gray-300'
                : 'bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed border-violet-700'
              }`}
          >
            {isRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isCompleted ? <RotateCcw className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            {isCompleted ? 'Re-run' : 'Run Stage'}
          </button>

          {/* Dropdown chevron — only for stages with extra run options */}
          {(onResume || onRetryFailed) && (
            <button
              onClick={() => setDropdownOpen((prev) => !prev)}
              disabled={isRunning || runLoading || resumeLoading || retryFailedLoading || disableRun}
              title="More run options"
              className={`inline-flex items-center px-1.5 py-1.5 rounded-r-lg text-xs font-medium transition-colors ${isCompleted
                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
                : 'bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          )}

          {/* Dropdown panel — opens directly below the split button */}
          {dropdownOpen && (
            <div className="absolute left-0 top-full mt-1 z-20 min-w-[190px] rounded-lg border border-gray-200 bg-white shadow-lg py-1">
              <button
                onClick={() => { onRun(stageNumber); setDropdownOpen(false) }}
                disabled={isRunning || runLoading || disableRun}
                className="flex items-center gap-2 w-full px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCompleted ? <RotateCcw className="h-3.5 w-3.5 text-gray-500" /> : <Play className="h-3.5 w-3.5 text-gray-500" />}
                {isCompleted ? 'Re-run' : 'Run fresh'}
              </button>
              {onResume && (
                <button
                  onClick={() => { onResume(stageNumber); setDropdownOpen(false) }}
                  disabled={resumeLoading || disableRun}
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs text-amber-700 hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resumeLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
                  Resume
                </button>
              )}
              {onRetryFailed && (
                <button
                  onClick={() => { onRetryFailed(stageNumber); setDropdownOpen(false) }}
                  disabled={retryFailedLoading || disableRun}
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {retryFailedLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                  Retry failed
                </button>
              )}
            </div>
          )}
        </div>

        {/* Stop — inline with other buttons when running */}
        {isRunning && onCancel && (
          <button
            onClick={() => onCancel(stageNumber)}
            disabled={cancelLoading}
            title="Stop and cancel this running stage"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {cancelLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Square className="h-3.5 w-3.5" />}
            Stop
          </button>
        )}

        {/* Push download icons to the far right */}
        {isCompleted && <div className="flex-1" />}

        {/* Download JSON — icon only */}
        {isCompleted && (
          <button
            onClick={() => onDownloadJson(stageNumber)}
            title="Download JSON result"
            className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
          >
            <FileJson className="h-3.5 w-3.5" />
          </button>
        )}

        {/* Download Log — icon only */}
        {isCompleted && (
          <button
            onClick={() => onDownloadLog(stageNumber)}
            title="Download stage log"
            className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
          >
            <FileText className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Stage last-run tooltip (grid) ────────────────────────────────────────────

function StageLastRunTooltip({ stages }: { stages: LarsStageStatus[] }) {
  const [open, setOpen] = useState(false)

  return (
    <div
      className="relative inline-flex flex-shrink-0"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="text-gray-300 hover:text-gray-500 transition-colors"
        aria-label="Show last run time per stage"
        tabIndex={0}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute z-20 bottom-full right-0 mb-2 w-60 rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-lg text-left pointer-events-none"
        >
          <p className="text-xs font-semibold text-gray-700 mb-2">Last run per stage</p>
          <ul className="space-y-1.5">
            {[1, 2, 3, 4].map((n) => {
              const stage = stages.find((s) => s.stage_number === n)
              const ts = stage?.last_updated
              return (
                <li key={n} className="text-xs">
                  <span className="font-medium text-gray-600">{n}. {STAGE_NAMES[n]}</span>
                  <span className="block text-gray-400 mt-0.5">
                    {ts ? (
                      <>
                        {formatAbsoluteTime(ts)}
                        <span className="ml-1">({formatRelativeTime(ts)})</span>
                      </>
                    ) : (
                      'Not run yet'
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Lars() {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // ── Lazy-init from localStorage so state starts with the correct value, not {}.
  // This prevents React 18's double-mount from deleting localStorage before the
  // restore effect can read it (the double-mount resets state to {} but keeps refs,
  // so the old "skip on first render" ref hack would no longer fire and would wipe
  // the stored data on the second mount).
  const [activeJobs, setActiveJobs] = useState<Record<string, ActiveJob>>(() => {
    try {
      const stored = localStorage.getItem('lars_active_jobs')
      if (!stored) return {}
      const parsed = JSON.parse(stored)
      return typeof parsed === 'object' && parsed !== null ? parsed : {}
    } catch {
      return {}
    }
  })

  const { data: tenants = [], isLoading: tenantsLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: apiClient.getTenants,
  })

  // Poll getLarsRuns automatically while any stage is actively running.
  // refetchInterval re-evaluates on every render, so it activates as soon as
  // activeJobs becomes non-empty and deactivates when it empties.
  const { data: larsData, isLoading: larsLoading, isFetching: larsFetching, refetch: refetchRuns } = useQuery<LarsRunsListResponse>({
    queryKey: ['lars-runs'],
    queryFn: async () => {
      const data = await apiClient.getLarsRuns({ summary: true })
      writeLarsRunsCache(data)
      return data
    },
    retry: false,
    staleTime: LARS_STALE_MS,
    cacheTime: 30 * 60 * 1000,
    initialData: readLarsRunsCache,
    keepPreviousData: true,
    refetchInterval: Object.keys(activeJobs).length > 0 ? POLL_INTERVAL_MS : false,
  })

  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(
    () => sessionStorage.getItem('lars_tenant_id'),
  )
  type LabsView = 'grid' | 'template-settings' | 'run-detail' | 'run-settings'
  const [view, setView] = useState<LabsView>(
    () => (sessionStorage.getItem('lars_view') as LabsView) ?? 'grid',
  )
  // Tenant display layout — list (default) or grid.
  type LabsLayout = 'list' | 'grid'
  const [layout, setLayout] = useState<LabsLayout>(
    () => (sessionStorage.getItem('lars_layout') as LabsLayout) ?? 'list',
  )

  // ── Config save state ──────────────────────────────────────────────────────
  const [templateSaveSuccess, setTemplateSaveSuccess] = useState(false)
  const [templateSaveError, setTemplateSaveError] = useState<string | null>(null)
  const [runConfigSaveSuccess, setRunConfigSaveSuccess] = useState(false)
  const [runConfigSaveError, setRunConfigSaveError] = useState<string | null>(null)

  const PAGE_SIZE = 9
  const [stageResults, setStageResults] = useState<Record<string, StageResultCache>>({})
  const [sdlInfo, setSdlInfo] = useState<Record<string, { count: number; uploadedAt: string }>>({})
  const [actionError, setActionError] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [runAllActive, setRunAllActive] = useState(false)
  const runAllRunNameRef = useRef<string | null>(null)
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null)

  const runsMap: Record<string, LarsRun> = {}
  larsData?.runs.forEach((r) => { runsMap[r.run_name] = r })

  const selectedTenant = tenants.find((t) => t.id === selectedTenantId) ?? null
  const summaryRun = selectedTenant ? runsMap[selectedTenant.subdomain] ?? null : null

  const { data: runDetail } = useQuery({
    queryKey: ['lars-run', selectedTenant?.subdomain],
    queryFn: () => apiClient.getLarsRun(selectedTenant!.subdomain),
    enabled: !!selectedTenant && !!summaryRun,
    staleTime: LARS_STALE_MS,
    retry: false,
  })

  const selectedRun = runDetail ?? summaryRun
  const selectedActiveJob = selectedTenant ? activeJobs[selectedTenant.subdomain] ?? null : null

  const filteredTenants = tenants.filter((t) => {
    if (!search) return true
    const q = search.toLowerCase()
    return t.name.toLowerCase().includes(q) || t.subdomain.toLowerCase().includes(q)
  })

  useEffect(() => { setCurrentPage(1) }, [search])

  const totalPages = Math.ceil(filteredTenants.length / PAGE_SIZE)
  const pagedTenants = filteredTenants.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
  const showingFrom = filteredTenants.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
  const showingTo = Math.min(currentPage * PAGE_SIZE, filteredTenants.length)

  const pollTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  const pollJob = useCallback(
    async (runName: string, jobId: string, stageNumber: number) => {
      try {
        const job: LarsJob = await apiClient.getLarsJobStatus(jobId)
        if (job.status === 'completed') {
          // Await fresh run data before removing the active job so the UI transitions
          // directly from "Running" → "Completed" without a "Not started" flicker.
          await refetchRuns()
          queryClient.invalidateQueries({ queryKey: ['lars-run', runName] })
          setActiveJobs((prev) => {
            const next = { ...prev }
            delete next[runName]
            return next
          })
          try {
            const result = await apiClient.getLarsStageResult(runName, stageNumber)
            setStageResults((prev) => ({
              ...prev,
              [runName]: { ...(prev[runName] ?? {}), [stageNumber]: result },
            }))
          } catch {
            // result not critical
          }
          // Stage 4 completion triggers a server-side push of the fresh reports into
          // the client DB — refetch the synced reports so the table and visibility
          // banner update without a page refresh. The push starts right after the job
          // flips to completed, so invalidate again shortly after in case the first
          // refetch races the DB write.
          if (stageNumber === 4) {
            const invalidateReports = () => {
              queryClient.invalidateQueries({ queryKey: ['lars-reports'] })
              queryClient.invalidateQueries({ queryKey: ['lars-authority', runName] })
            }
            invalidateReports()
            setTimeout(invalidateReports, 5000)
          }
          // ── Run All: chain to next stage ──
          if (runAllRunNameRef.current === runName) {
            if (stageNumber < 4) {
              const nextStage = stageNumber + 1
              try {
                const nextData = await apiClient.runLarsStage(runName, nextStage)
                setActiveJobs((prev) => ({
                  ...prev,
                  [runName]: { jobId: nextData.job_id, stageNumber: nextStage },
                }))
                pollTimers.current[runName] = setTimeout(
                  () => pollJob(runName, nextData.job_id, nextStage),
                  POLL_INTERVAL_MS
                )
              } catch (e: any) {
                runAllRunNameRef.current = null
                setRunAllActive(false)
                setActionError(`Run All stopped at stage ${nextStage}: ${e.message}`)
              }
            } else {
              runAllRunNameRef.current = null
              setRunAllActive(false)
            }
          }
        } else if (job.status === 'failed') {
          await refetchRuns()
          queryClient.invalidateQueries({ queryKey: ['lars-run', runName] })
          setActiveJobs((prev) => {
            const next = { ...prev }
            delete next[runName]
            return next
          })
          if (runAllRunNameRef.current === runName) {
            runAllRunNameRef.current = null
            setRunAllActive(false)
          }
          setActionError(`Stage ${stageNumber} failed: ${job.error ?? 'Unknown error'}`)
        } else if (job.status === 'cancelled') {
          setActiveJobs((prev) => {
            const next = { ...prev }
            delete next[runName]
            return next
          })
          if (runAllRunNameRef.current === runName) {
            runAllRunNameRef.current = null
            setRunAllActive(false)
          }
          clearTimeout(pollTimers.current[runName])
        } else {
          pollTimers.current[runName] = setTimeout(
            () => pollJob(runName, jobId, stageNumber),
            POLL_INTERVAL_MS
          )
        }
      } catch {
        pollTimers.current[runName] = setTimeout(
          () => pollJob(runName, jobId, stageNumber),
          POLL_INTERVAL_MS
        )
      }
    },
    [refetchRuns, queryClient]
  )

  useEffect(() => {
    return () => { Object.values(pollTimers.current).forEach(clearTimeout) }
  }, [])

  // ── Keep a stable ref to pollJob so the mount effect can call it safely ───
  const pollJobRef = useRef(pollJob)
  useEffect(() => { pollJobRef.current = pollJob }, [pollJob])

  // ── Persist activeJobs to localStorage whenever it changes ────────────────
  // No skip-on-first-render needed — activeJobs is lazy-init'd from localStorage,
  // so the initial value is already correct and writing it back is a safe no-op.
  useEffect(() => {
    if (Object.keys(activeJobs).length > 0) {
      localStorage.setItem('lars_active_jobs', JSON.stringify(activeJobs))
    } else {
      localStorage.removeItem('lars_active_jobs')
    }
  }, [activeJobs])

  // ── Persist view + selectedTenantId to sessionStorage ─────────────────────
  useEffect(() => {
    sessionStorage.setItem('lars_view', view)
    sessionStorage.setItem('lars_layout', layout)
    if (selectedTenantId) {
      sessionStorage.setItem('lars_tenant_id', selectedTenantId)
    } else {
      sessionStorage.removeItem('lars_tenant_id')
    }
  }, [view, layout, selectedTenantId])

  // ── On mount, restart getLarsJobStatus polling for any restored active jobs ─
  // activeJobs here is the lazy-init value from localStorage (not {}).
  // refetchInterval on getLarsRuns (above) handles the getLarsRuns polling;
  // this effect restarts the per-job status polling.
  useEffect(() => {
    Object.entries(activeJobs).forEach(([runName, job]) => {
      clearTimeout(pollTimers.current[runName])
      pollTimers.current[runName] = setTimeout(
        () => pollJobRef.current(runName, job.jobId, job.stageNumber),
        POLL_INTERVAL_MS,
      )
    })
  }, []) // intentionally mount-only; activeJobs captured from lazy-init closure

  // ── Reconcile: when larsData updates, clear activeJobs entries whose run ───
  // no longer exists (deleted) or whose stage is already completed. ──────────
  useEffect(() => {
    if (!larsData) return
    const map: Record<string, LarsRun> = {}
    larsData.runs.forEach((r) => { map[r.run_name] = r })
    setActiveJobs((prev) => {
      const next = { ...prev }
      let changed = false
      for (const [runName, job] of Object.entries(next)) {
        const run = map[runName]
        if (!run) {
          // Run was deleted — stop polling
          delete next[runName]
          clearTimeout(pollTimers.current[runName])
          delete pollTimers.current[runName]
          changed = true
          continue
        }
        const stage = run.stages.find((s) => s.stage_number === job.stageNumber)
        if (stage?.completed) {
          delete next[runName]
          clearTimeout(pollTimers.current[runName])
          delete pollTimers.current[runName]
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [larsData])

  useEffect(() => {
    if (!selectedRun) return
    const runName = selectedRun.run_name
    if (stageResults[runName]) return
    selectedRun.stages.forEach(async (s) => {
      if (s.completed) {
        try {
          const result = await apiClient.getLarsStageResult(runName, s.stage_number)
          setStageResults((prev) => ({
            ...prev,
            [runName]: { ...(prev[runName] ?? {}), [s.stage_number]: result },
          }))
        } catch {
          // stage result not available yet
        }
      }
    })
  }, [selectedRun?.run_name, selectedRun?.completed_count])

  // ── Mutations ──────────────────────────────────────────────────────────────

  const createRunMutation = useMutation({
    mutationFn: ({ subdomain, name }: { subdomain: string; name: string }) =>
      apiClient.createLarsRun({ run_name: subdomain, config_overrides: { brand_name: name } }),
    onSuccess: (_data, { subdomain }) => {
      // Optimistically add the new run to the cache so selectedRun becomes non-null
      // immediately — no "No LARS run yet" flash while the background GET is in flight.
      queryClient.setQueryData(['lars-runs'], (old: any) => {
        if (!old) return old
        const optimisticRun: LarsRun = {
          run_name: subdomain,
          completed_count: 0,
          stages: [],
          sdl_exists: false,
          sdl_question_count: null,
          total_stages: 4,
          log_count: 0,
          recent_logs: [],
        }
        const existing = old.runs ?? []
        const already = existing.find((r: LarsRun) => r.run_name === subdomain)
        return { ...old, runs: already ? existing : [...existing, optimisticRun] }
      })
      clearLarsRunsCache()
      queryClient.invalidateQueries({ queryKey: ['lars-runs'] })
      setActionError(null)
    },
    onError: (e: Error) => setActionError(e.message),
  })

  const deleteRunMutation = useMutation({
    mutationFn: (runName: string) => apiClient.deleteLarsRun(runName),
    onSuccess: async (_data, runName) => {
      // Clear any active job + timer for this run immediately so polling stops
      setActiveJobs((prev) => {
        const next = { ...prev }
        delete next[runName]
        return next
      })
      clearTimeout(pollTimers.current[runName])
      delete pollTimers.current[runName]

      // Optimistically remove the run from the cache so the UI transitions to
      // the empty state immediately — no stale run card visible while GET is in flight.
      queryClient.setQueryData(['lars-runs'], (old: any) => {
        if (!old) return old
        return { ...old, runs: (old.runs ?? []).filter((r: LarsRun) => r.run_name !== runName) }
      })

      if (selectedTenantId) {
        try {
          await apiClient.deleteLarsReportsByRun(selectedTenantId, runName)
          queryClient.invalidateQueries({ queryKey: ['lars-reports', selectedTenantId] })
        } catch {
          // Non-fatal
        }
      }
      clearLarsRunsCache()
      queryClient.invalidateQueries({ queryKey: ['lars-runs'] })
      queryClient.invalidateQueries({ queryKey: ['lars-run', runName] })
      setShowDeleteModal(false)
      setActionError(null)
    },
    onError: (e: Error) => setActionError(e.message),
  })

  const runStageMutation = useMutation({
    mutationFn: ({ runName, stage }: { runName: string; stage: number }) =>
      apiClient.runLarsStage(runName, stage),
    onSuccess: (data, { runName, stage }) => {
      setActiveJobs((prev) => ({
        ...prev,
        [runName]: { jobId: data.job_id, stageNumber: stage },
      }))
      setActionError(null)
      pollTimers.current[runName] = setTimeout(
        () => pollJob(runName, data.job_id, stage),
        POLL_INTERVAL_MS
      )
    },
    onError: (e: Error) => setActionError(e.message),
  })

  const resumeStageMutation = useMutation({
    mutationFn: ({ runName, stage }: { runName: string; stage: number }) =>
      apiClient.runLarsStage(runName, stage, true),
    onSuccess: (data, { runName, stage }) => {
      setActiveJobs((prev) => ({
        ...prev,
        [runName]: { jobId: data.job_id, stageNumber: stage },
      }))
      setActionError(null)
      pollTimers.current[runName] = setTimeout(
        () => pollJob(runName, data.job_id, stage),
        POLL_INTERVAL_MS
      )
    },
    onError: (e: Error) => setActionError(e.message),
  })

  const retryFailedMutation = useMutation({
    mutationFn: ({ runName, stage }: { runName: string; stage: number }) =>
      apiClient.runLarsStage(runName, stage, false, true),
    onSuccess: (data, { runName, stage }) => {
      setActiveJobs((prev) => ({
        ...prev,
        [runName]: { jobId: data.job_id, stageNumber: stage },
      }))
      setActionError(null)
      pollTimers.current[runName] = setTimeout(
        () => pollJob(runName, data.job_id, stage),
        POLL_INTERVAL_MS
      )
    },
    onError: (e: Error) => setActionError(e.message),
  })

  const cancelJobMutation = useMutation({
    mutationFn: (jobId: string) => apiClient.cancelLarsJob(jobId),
    onSuccess: (_data, _jobId, _context) => {
      setActionError(null)
    },
    onError: (e: Error) => setActionError(e.message),
  })

  // ── Loading flags: only active during the actual mutation call ─────────────
  // We do NOT extend these with larsFetching — doing so caused them to fire on
  // every background poll (every 3s), disrupting the UI.
  // The POST→GET gap is bridged via optimistic cache writes (see mutations below).
  const isCreatingRun = createRunMutation.isLoading
  const isDeletingRun = deleteRunMutation.isLoading

  // ── LARS Reports (stored in ai-api DB) ────────────────────────────────────

  const { data: larsReports = [], isLoading: reportsLoading } = useQuery({
    queryKey: ['lars-reports', selectedTenantId],
    queryFn: () => apiClient.getLarsReportsForTenant(selectedTenantId!),
    enabled: !!selectedTenantId,
    retry: false,
  })

  const [showDeleteReportModal, setShowDeleteReportModal] = useState(false)
  const [deletingReport, setDeletingReport] = useState<LarsReportSummary | null>(null)

  const toggleVisibilityMutation = useMutation({
    mutationFn: ({ report, isViewable }: { report: LarsReportSummary; isViewable: boolean }) =>
      apiClient.toggleLarsReportVisibility(report.run_name, selectedTenantId!, isViewable),
    onSuccess: (_data, { report }) => {
      queryClient.invalidateQueries({ queryKey: ['lars-reports', selectedTenantId] })
      queryClient.invalidateQueries({ queryKey: ['lars-authority', report.run_name] })
      setActionError(null)
    },
    onError: (e: Error) => setActionError(e.message),
  })

  const deleteReportMutation = useMutation({
    mutationFn: (report: LarsReportSummary) =>
      apiClient.deleteLarsReportFromAdmin(report.id, report.run_name, selectedTenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lars-reports', selectedTenantId] })
      setShowDeleteReportModal(false)
      setDeletingReport(null)
      setActionError(null)
    },
    onError: (e: Error) => setActionError(e.message),
  })

  // ── SDL upload ─────────────────────────────────────────────────────────────

  const [sdlUploading, setSdlUploading] = useState(false)
  const [sdlUploadError, setSdlUploadError] = useState<string | null>(null)

  const handleSdlFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !selectedRun) return
    setSdlUploading(true)
    setSdlUploadError(null)
    try {
      const text = await file.text()
      const json = JSON.parse(text)
      const res = await apiClient.uploadLarsSDL(selectedRun.run_name, json)
      setSdlInfo((prev) => ({
        ...prev,
        [selectedRun.run_name]: { count: res.question_count, uploadedAt: new Date().toISOString() },
      }))
      refetchRuns()
      clearLarsRunsCache()
      queryClient.invalidateQueries({ queryKey: ['lars-runs'] })
      if (selectedRun) {
        queryClient.invalidateQueries({ queryKey: ['lars-run', selectedRun.run_name] })
      }
    } catch (err: any) {
      setSdlUploadError(err.message ?? 'Failed to upload SDL')
    } finally {
      setSdlUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  // ── Download helpers ───────────────────────────────────────────────────────

  const handleDownloadStageJson = async (runName: string, stageNumber: number) => {
    try {
      const cached = stageResults[runName]?.[stageNumber]
      if (cached) { downloadJson(cached, `stage${stageNumber}_${runName}.json`); return }
      const result = await apiClient.getLarsStageResult(runName, stageNumber)
      setStageResults((prev) => ({
        ...prev,
        [runName]: { ...(prev[runName] ?? {}), [stageNumber]: result },
      }))
      downloadJson(result, `stage${stageNumber}_${runName}.json`)
    } catch (e: any) { setActionError(e.message) }
  }

  const handleDownloadLog = async (runName: string, stageNumber: number) => {
    try { await apiClient.downloadLarsStageLog(runName, stageNumber) }
    catch (e: any) { setActionError(e.message) }
  }

  const handleDownloadReport = async (runName: string) => {
    try { await apiClient.downloadLarsReport(runName) }
    catch (e: any) { setActionError(e.message) }
  }

  const handleDownloadReportForRow = async (report: LarsReportSummary) => {
    setDownloadingReportId(report.id)
    try { await apiClient.downloadLarsReportByReportId(report.run_name, report.lars_report_id) }
    catch (e: any) {
      if (e.message?.includes('404') || e.message?.toLowerCase().includes('not found')) {
        setActionError(`No archived PDF for this report — it was generated before versioned archiving was introduced. Re-run stage 4 to create a new archived copy.`)
      } else {
        setActionError(e.message)
      }
    }
    finally { setDownloadingReportId(null) }
  }

  const handleRunAll = () => {
    if (!selectedRun || runAllActive || !!selectedActiveJob) return
    const runName = selectedRun.run_name
    runAllRunNameRef.current = runName
    setRunAllActive(true)
    setActionError(null)
    runStageMutation.mutate({ runName, stage: 1 })
  }

  // ── UI helpers ─────────────────────────────────────────────────────────────

  const getRunStatusForTenant = (subdomain: string) => {
    const run = runsMap[subdomain]
    if (!run) return 'none'
    if (activeJobs[subdomain]) return 'running'
    if (run.completed_count === 4) return 'complete'
    return 'in_progress'
  }

  const runStatusBadge = (status: string) => {
    const map: Record<string, { label: string; cls: string }> = {
      none: { label: 'No run', cls: 'bg-gray-100 text-gray-500' },
      in_progress: { label: 'In progress', cls: 'bg-violet-100 text-violet-700' },
      running: { label: 'Running', cls: 'bg-amber-100 text-amber-700' },
      complete: { label: 'Complete', cls: 'bg-emerald-100 text-emerald-700' },
    }
    const { label, cls } = map[status] ?? map.none
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
        {label}
      </span>
    )
  }

  const stage4Completed = selectedRun?.stages.find((s) => s.stage_number === 4)?.completed ?? false
  const selectedSdlInfo = selectedRun ? sdlInfo[selectedRun.run_name] : null

  const hasLarsData = larsData !== undefined
  const showGridSpinner = tenantsLoading || (larsLoading && !hasLarsData)

  // ── SDL status — sourced directly from the run data ───────────────────────

  const hasSdl = !!selectedSdlInfo || selectedRun?.sdl_exists === true
  const sdlQuestionCount = selectedSdlInfo?.count ?? selectedRun?.sdl_question_count ?? null

  // ── Live authority report state (from LARS JSON, not DB) ──────────────────

  const { data: authorityReport, isLoading: authorityLoading, refetch: refetchAuthority } = useQuery({
    queryKey: ['lars-authority', selectedRun?.run_name],
    queryFn: () => apiClient.getLarsAuthorityReport(selectedRun!.run_name),
    enabled: !!selectedRun && stage4Completed,
    retry: false,
  })

  const toggleRunVisibilityMutation = useMutation({
    mutationFn: ({ runName, isViewable }: { runName: string; isViewable: boolean }) =>
      apiClient.toggleLarsReportVisibility(runName, selectedTenantId!, isViewable),
    onSuccess: () => {
      refetchAuthority()
      queryClient.invalidateQueries({ queryKey: ['lars-reports', selectedTenantId] })
      setActionError(null)
    },
    onError: (e: Error) => setActionError(e.message),
  })

  // ── LARS Config — template (global) ───────────────────────────────────────

  const { data: templateConfig = null, isLoading: templateConfigLoading } = useQuery({
    queryKey: ['lars-template-config'],
    queryFn: apiClient.getLarsTemplateConfig,
    enabled: view === 'template-settings',
    retry: false,
  })

  const updateTemplateConfigMutation = useMutation({
    mutationFn: (config: Record<string, any>) => apiClient.updateLarsTemplateConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lars-template-config'] })
      setTemplateSaveSuccess(true)
      setTemplateSaveError(null)
      setTimeout(() => setTemplateSaveSuccess(false), 4000)
    },
    onError: (e: Error) => {
      setTemplateSaveError(e.message)
      setTemplateSaveSuccess(false)
    },
  })

  // ── LARS Config — per-run ──────────────────────────────────────────────────

  const { data: runConfig = null, isLoading: runConfigLoading } = useQuery({
    queryKey: ['lars-run-config', selectedRun?.run_name],
    queryFn: () => apiClient.getLarsRunConfig(selectedRun!.run_name),
    enabled: view === 'run-settings' && !!selectedRun,
    retry: false,
  })

  const updateRunConfigMutation = useMutation({
    mutationFn: (config: Record<string, any>) =>
      apiClient.updateLarsRunConfig(selectedRun!.run_name, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lars-run-config', selectedRun?.run_name] })
      setRunConfigSaveSuccess(true)
      setRunConfigSaveError(null)
      setTimeout(() => setRunConfigSaveSuccess(false), 4000)
    },
    onError: (e: Error) => {
      setRunConfigSaveError(e.message)
      setRunConfigSaveSuccess(false)
    },
  })

  // ── Render ─────────────────────────────────────────────────────────────────

  // ── VIEW: Template settings ────────────────────────────────────────────────
  if (view === 'template-settings') {
    return (
      <div className="max-w-3xl">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => { setView('grid'); setTemplateSaveSuccess(false); setTemplateSaveError(null) }}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Labs / LARS
          </button>
          <span className="text-gray-300">/</span>
          <span className="text-sm font-medium text-gray-900">Settings</span>
        </div>

        {/* Context banner */}
        <div className="mb-6 rounded-2xl border border-violet-100 bg-violet-50/60 px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-violet-100">
              <Settings2 className="h-4 w-4 text-violet-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-violet-900">Template Config — Global Defaults</p>
              <p className="text-xs text-violet-600 mt-0.5">
                Changes here update <code className="font-mono bg-violet-100 px-1 rounded">config.sample.json</code> and affect all newly created runs
              </p>
            </div>
          </div>
        </div>

        <LarsConfigEditor
          config={templateConfig}
          isLoading={templateConfigLoading}
          isSaving={updateTemplateConfigMutation.isLoading}
          saveError={templateSaveError}
          saveSuccess={templateSaveSuccess}
          onSave={(config) => updateTemplateConfigMutation.mutate(config)}
          onDismissSuccess={() => setTemplateSaveSuccess(false)}
          onDismissError={() => setTemplateSaveError(null)}
        />
      </div>
    )
  }

  // ── VIEW: Run settings ─────────────────────────────────────────────────────
  if (view === 'run-settings' && selectedTenant && selectedRun) {
    return (
      <div className="max-w-3xl">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => { setView('run-detail'); setRunConfigSaveSuccess(false); setRunConfigSaveError(null) }}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {selectedTenant.name}
          </button>
          <span className="text-gray-300">/</span>
          <span className="text-sm font-medium text-gray-900">Settings</span>
        </div>

        {/* Context banner */}
        <div className="mb-6 rounded-2xl border border-gray-200 bg-white px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-gray-100">
              <Settings2 className="h-4 w-4 text-gray-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-gray-900">Run Config</p>
                <span className="text-xs font-mono bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{selectedRun.run_name}</span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Changes here only affect this run — applied on the next stage execution
              </p>
            </div>
          </div>
        </div>

        <LarsConfigEditor
          config={runConfig}
          isLoading={runConfigLoading}
          isSaving={updateRunConfigMutation.isLoading}
          saveError={runConfigSaveError}
          saveSuccess={runConfigSaveSuccess}
          onSave={(config) => updateRunConfigMutation.mutate(config)}
          onDismissSuccess={() => setRunConfigSaveSuccess(false)}
          onDismissError={() => setRunConfigSaveError(null)}
        />
      </div>
    )
  }

  // ── VIEW 1: Tenant grid ────────────────────────────────────────────────────
  if (!selectedTenant) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-100">
              <FlaskConical className="h-6 w-6 text-violet-600" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <span className="text-2xl font-bold text-gray-900">Labs</span>
                <ChevronRight className="h-5 w-5 text-gray-400 mt-0.5" />
                <span className="text-2xl font-bold text-gray-900">LARS</span>
              </div>
              <p className="text-sm text-gray-500">AI Response Scoring — manage runs across all tenants</p>
            </div>
          </div>
          <button
            onClick={() => setView('template-settings')}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:border-violet-300 transition-colors"
          >
            <Settings2 className="h-4 w-4" />
            Settings
          </button>
        </div>

        {actionError && (
          <div className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="flex-1">{actionError}</span>
            <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-600 font-medium">✕</button>
          </div>
        )}

        <div className="mb-5 flex items-center justify-between gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search tenants…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
            />
          </div>
          <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden flex-shrink-0">
            <button
              type="button"
              onClick={() => setLayout('list')}
              title="List view"
              className={`inline-flex items-center justify-center h-9 w-9 transition-colors ${layout === 'list'
                ? 'bg-violet-50 text-violet-600'
                : 'bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                }`}
            >
              <List className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setLayout('grid')}
              title="Grid view"
              className={`inline-flex items-center justify-center h-9 w-9 border-l border-gray-200 transition-colors ${layout === 'grid'
                ? 'bg-violet-50 text-violet-600'
                : 'bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                }`}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>
        </div>

        {larsFetching && hasLarsData && (
          <div className="mb-4 flex items-center gap-2 text-xs text-gray-400">
            <Loader2 className="h-3 w-3 animate-spin" />
            Refreshing run status…
          </div>
        )}

        {showGridSpinner ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span className="text-sm">Loading tenants…</span>
          </div>
        ) : filteredTenants.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-20">No tenants found</p>
        ) : (
          <>
            {layout === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {pagedTenants.map((tenant) => {
                  const status = getRunStatusForTenant(tenant.subdomain)
                  const run = runsMap[tenant.subdomain]
                  return (
                    <button
                      key={tenant.id}
                      onClick={() => {
                        setSelectedTenantId(tenant.id)
                        setActionError(null)
                        setSdlUploadError(null)
                      }}
                      className="group text-left rounded-2xl border border-gray-200 bg-white px-5 py-4 hover:border-violet-300 hover:shadow-sm transition-all duration-150"
                    >
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-50 flex-shrink-0">
                          <FlaskConical className="h-4 w-4 text-violet-500" />
                        </div>
                        {runStatusBadge(status)}
                      </div>
                      <p className="text-sm font-semibold text-gray-900 truncate">{tenant.name}</p>
                      <p className="text-xs text-gray-400 truncate mt-0.5">{tenant.subdomain}</p>
                      {run ? (
                        <div className="mt-3 flex items-start gap-1.5">
                          <div className="flex-1 min-w-0">
                            <RunProgressBar completed={run.completed_count} total={4} />
                            <p className="text-xs text-gray-400 mt-1">{run.completed_count}/4 stages complete</p>
                          </div>
                          <StageLastRunTooltip stages={run.stages} />
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 mt-3">No run created yet</p>
                      )}
                      <div className="mt-4 flex items-center gap-1 text-xs font-medium text-violet-600 group-hover:text-violet-700 transition-colors">
                        <span>Manage</span>
                        <ChevronRight className="h-3.5 w-3.5" />
                      </div>
                    </button>
                  )
                })}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {pagedTenants.map((tenant) => {
                  const status = getRunStatusForTenant(tenant.subdomain)
                  const run = runsMap[tenant.subdomain]
                  return (
                    <button
                      key={tenant.id}
                      onClick={() => {
                        setSelectedTenantId(tenant.id)
                        setActionError(null)
                        setSdlUploadError(null)
                      }}
                      className="group flex items-center gap-4 w-full text-left rounded-xl border border-gray-200 bg-white px-4 py-3 hover:border-violet-300 hover:shadow-sm transition-all duration-150"
                    >
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-50 flex-shrink-0">
                        <FlaskConical className="h-4 w-4 text-violet-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">{tenant.name}</p>
                        <p className="text-xs text-gray-400 truncate mt-0.5">{tenant.subdomain}</p>
                      </div>
                      <div className="hidden sm:flex sm:items-start sm:gap-1.5 w-48 flex-shrink-0">
                        {run ? (
                          <>
                            <div className="flex-1 min-w-0">
                              <RunProgressBar completed={run.completed_count} total={4} />
                              <p className="text-xs text-gray-400 mt-1">{run.completed_count}/4 stages complete</p>
                            </div>
                            <StageLastRunTooltip stages={run.stages} />
                          </>
                        ) : (
                          <p className="text-xs text-gray-400">No run created yet</p>
                        )}
                      </div>
                      <div className="flex-shrink-0">{runStatusBadge(status)}</div>
                      <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-violet-500 flex-shrink-0 transition-colors" />
                    </button>
                  )
                })}
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing <span className="font-medium text-gray-700">{showingFrom}–{showingTo}</span> of{' '}
                  <span className="font-medium text-gray-700">{filteredTenants.length}</span> tenants
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </button>
                  <span className="text-sm text-gray-500 px-1">{currentPage} / {totalPages}</span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  // ── VIEW 2: Run detail ─────────────────────────────────────────────────────
  return (
    <div>
      {/* Header with back button */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => {
            setSelectedTenantId(null)
            setActionError(null)
            setSdlUploadError(null)
            setShowDeleteModal(false)
          }}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          All Tenants
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-sm font-medium text-gray-900">{selectedTenant.name}</span>
      </div>

      {actionError && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{actionError}</span>
          <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-600 font-medium">✕</button>
        </div>
      )}

      {isCreatingRun || isDeletingRun ? (
        <div className="flex items-center justify-center py-24 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2.5" />
          <span className="text-sm">
            {isCreatingRun ? 'Creating run…' : 'Deleting run…'}
          </span>
        </div>
      ) : larsLoading && !hasLarsData ? (
        <div className="flex items-center justify-center py-24 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2.5" />
          <span className="text-sm">Loading run…</span>
        </div>
      ) : !selectedRun ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-10 py-16">
          <div className="flex flex-col items-center text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-50">
              <FlaskConical className="h-7 w-7 text-violet-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-800">{selectedTenant.name}</h3>
            <p className="text-sm text-gray-500 mt-1 mb-6">{selectedTenant.subdomain} · No LARS run yet</p>
            <button
              onClick={() => createRunMutation.mutate({ subdomain: selectedTenant.subdomain, name: selectedTenant.name })}
              disabled={isCreatingRun}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-60 transition-colors"
            >
              {isCreatingRun ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {isCreatingRun ? 'Creating run…' : 'Create LARS Run'}
            </button>
            {createRunMutation.isError && (
              <p className="text-xs text-red-500 mt-3">{(createRunMutation.error as Error).message}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-5 max-w-3xl">
          {/* Run header card */}
          <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4 flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-gray-900">{selectedTenant.name}</h2>
                <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded">
                  {selectedRun.run_name}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-0.5">{selectedTenant.subdomain}</p>
            </div>
            <div className="flex items-center gap-3">
              {runStatusBadge(getRunStatusForTenant(selectedTenant.subdomain))}
              <button
                onClick={() => setView('run-settings')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-violet-300 transition-colors"
              >
                <Settings2 className="h-3.5 w-3.5" />
                Run Config
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                disabled={isDeletingRun}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isDeletingRun ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
                {isDeletingRun ? 'Deleting…' : 'Delete Run'}
              </button>
            </div>
          </div>

          {/* SDL card */}
          <div className={`rounded-2xl border bg-white px-5 py-4 ${hasSdl ? 'border-emerald-200' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900">SDL Data</h3>
                {hasSdl && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" />
                    Synced
                  </span>
                )}
              </div>
              {selectedSdlInfo && (
                <span className="text-xs text-gray-400">
                  {selectedSdlInfo.count} questions · Uploaded {formatRelativeTime(selectedSdlInfo.uploadedAt)}
                </span>
              )}
              {!selectedSdlInfo && selectedRun?.sdl_exists && sdlQuestionCount != null && (
                <span className="text-xs text-gray-400">{sdlQuestionCount} questions</span>
              )}
            </div>
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`relative flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl px-4 py-6 cursor-pointer transition-colors ${sdlUploading
                ? 'border-violet-300 bg-violet-50'
                : hasSdl
                  ? 'border-emerald-200 bg-emerald-50/30 hover:border-emerald-300 hover:bg-emerald-50/50'
                  : 'border-gray-200 hover:border-violet-300 hover:bg-violet-50/30'
                }`}
            >
              {sdlUploading ? (
                <>
                  <Loader2 className="h-6 w-6 text-violet-500 animate-spin" />
                  <p className="text-sm text-violet-600 font-medium">Uploading SDL…</p>
                </>
              ) : selectedSdlInfo ? (
                <>
                  <div className="flex items-center gap-2">
                    <FileJson className="h-5 w-5 text-emerald-500" />
                    <span className="text-sm font-medium text-gray-700">sdl_data.json</span>
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                      {selectedSdlInfo.count} questions
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <Upload className="h-3.5 w-3.5" />
                    <span>Click to re-upload SDL</span>
                  </div>
                </>
              ) : selectedRun?.sdl_exists ? (
                <>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                    <span className="text-sm font-medium text-gray-700">sdl_data.json</span>
                    {sdlQuestionCount != null && (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                        {sdlQuestionCount} questions
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <Upload className="h-3.5 w-3.5" />
                    <span>SDL synced — click to re-upload</span>
                  </div>
                </>
              ) : (
                <>
                  <Upload className="h-6 w-6 text-gray-400" />
                  <p className="text-sm font-medium text-gray-600">Drop sdl_data.json here or click to browse</p>
                  <p className="text-xs text-gray-400">Download from: Client Admin → SDL Data tab → Download SDL</p>
                </>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept=".json,application/json" onChange={handleSdlFileChange} className="hidden" />
            {sdlUploadError && <p className="mt-2 text-xs text-red-500">{sdlUploadError}</p>}
          </div>

          {/* Pipeline */}
          <div>
            <div className="flex items-center justify-between mb-3 px-1">
              <h3 className="text-sm font-semibold text-gray-700">Pipeline</h3>
              <button
                onClick={handleRunAll}
                disabled={!hasSdl || runAllActive || !!selectedActiveJob || runStageMutation.isLoading}
                title={!hasSdl ? 'Upload SDL data before running the pipeline' : 'Run all stages sequentially (1 → 2 → 3 → 4)'}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {runAllActive ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <PlayCircle className="h-3.5 w-3.5" />
                )}
                {runAllActive ? 'Running all…' : 'Run All'}
              </button>
            </div>

            <div className="space-y-3">
              {[1, 2, 3, 4].map((stageNum) => {
                const stageStatus = selectedRun.stages.find((s) => s.stage_number === stageNum)
                const cachedResult = stageResults[selectedRun.run_name]?.[stageNum] ?? null
                const isThisStageRunning = selectedActiveJob?.stageNumber === stageNum
                return (
                  <StageCard
                    key={stageNum}
                    stageNumber={stageNum}
                    stageStatus={stageStatus}
                    activeJob={selectedActiveJob}
                    result={cachedResult}
                    runName={selectedRun.run_name}
                    onRun={(stage) => runStageMutation.mutate({ runName: selectedRun.run_name, stage })}
                    onResume={stageNum === 2 ? (stage) => resumeStageMutation.mutate({ runName: selectedRun.run_name, stage }) : undefined}
                    onRetryFailed={stageNum === 2 ? (stage) => retryFailedMutation.mutate({ runName: selectedRun.run_name, stage }) : undefined}
                    onCancel={isThisStageRunning && selectedActiveJob
                      ? (_stage) => cancelJobMutation.mutate(selectedActiveJob.jobId)
                      : undefined
                    }
                    onDownloadJson={(stage) => handleDownloadStageJson(selectedRun.run_name, stage)}
                    onDownloadLog={(stage) => handleDownloadLog(selectedRun.run_name, stage)}
                    runLoading={runStageMutation.isLoading}
                    resumeLoading={stageNum === 2 && resumeStageMutation.isLoading}
                    retryFailedLoading={stageNum === 2 && retryFailedMutation.isLoading}
                    cancelLoading={isThisStageRunning && cancelJobMutation.isLoading}
                    disableRun={!hasSdl && !stageStatus?.completed}
                    disableRunTitle={!hasSdl && !stageStatus?.completed ? 'Upload SDL data before running the pipeline' : undefined}
                  />
                )
              })}
            </div>
          </div>

          {/* Report banner */}
          {stage4Completed && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-100">
                    <FileDown className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">Report Ready</p>
                    <p className="text-xs text-emerald-600">Stage 4 complete — your LARS executive report is ready to download</p>
                  </div>
                </div>
                <button
                  onClick={() => handleDownloadReport(selectedRun.run_name)}
                  className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Download PDF Report
                </button>
              </div>

              {/* Authority report visibility toggle */}
              <div className="flex items-center justify-between pt-2 border-t border-emerald-200/60">
                <div>
                  <p className="text-xs font-medium text-emerald-800">Client Report Visibility</p>
                  <p className="text-xs text-emerald-600 mt-0.5">
                    Controls whether the authority report is shown on the client admin AEO dashboard
                  </p>
                </div>
                {authorityLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-emerald-600 flex-shrink-0" />
                ) : authorityReport ? (
                  <button
                    onClick={() =>
                      toggleRunVisibilityMutation.mutate({
                        runName: selectedRun.run_name,
                        isViewable: !authorityReport.is_viewable,
                      })
                    }
                    disabled={toggleRunVisibilityMutation.isLoading}
                    title={
                      authorityReport.is_viewable
                        ? 'Report is visible to client — click to hide'
                        : 'Report is hidden from client — click to show'
                    }
                    className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${authorityReport.is_viewable
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                      : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    {toggleRunVisibilityMutation.isLoading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : authorityReport.is_viewable ? (
                      <Eye className="h-3.5 w-3.5" />
                    ) : (
                      <EyeOff className="h-3.5 w-3.5" />
                    )}
                    {authorityReport.is_viewable ? 'Visible to Client' : 'Hidden from Client'}
                  </button>
                ) : (
                  <span className="text-xs text-emerald-600 italic">Authority report not yet fetched</span>
                )}
              </div>
            </div>
          )}

          {/* LARS Authority Reports */}
          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Authority Reports</h3>
                <p className="text-xs text-gray-400 mt-0.5">Stored LARS authority reports for this tenant</p>
              </div>
              {reportsLoading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
            </div>

            {larsReports.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <p className="text-sm text-gray-400">No authority reports stored yet.</p>
                <p className="text-xs text-gray-400 mt-1">Run stage 4 to generate and auto-sync a report.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Report ID</th>
                      <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Run</th>
                      <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                      <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Last Synced</th>
                      <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Viewable</th>
                      <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {larsReports.map((report: LarsReportSummary) => {
                      const isTogglingThis =
                        toggleVisibilityMutation.isLoading &&
                        (toggleVisibilityMutation.variables as any)?.report?.id === report.id
                      return (
                        <tr key={report.id} className="hover:bg-gray-50">
                          <td className="px-5 py-3">
                            <span className="font-mono text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                              {report.lars_report_id.slice(0, 8)}…
                            </span>
                          </td>
                          <td className="px-5 py-3 text-xs font-mono text-gray-700">{report.run_name}</td>
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${report.overall_grade.startsWith('A') ? 'bg-emerald-100 text-emerald-700' :
                              report.overall_grade.startsWith('B') ? 'bg-blue-100 text-blue-700' :
                                report.overall_grade.startsWith('C') ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                              }`}>
                              {report.overall_grade}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-xs text-gray-500">{report.last_synced || '—'}</td>
                          <td className="px-5 py-3 text-xs text-gray-500">{formatRelativeTime(report.created_at)}</td>
                          <td className="px-5 py-3">
                            <button
                              onClick={() =>
                                toggleVisibilityMutation.mutate({
                                  report,
                                  isViewable: !report.is_viewable,
                                })
                              }
                              disabled={isTogglingThis}
                              title={report.is_viewable ? 'Hide from client — click to set not viewable' : 'Show to client — click to set viewable'}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${report.is_viewable
                                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                }`}
                            >
                              {isTogglingThis ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : report.is_viewable ? (
                                <Eye className="h-3.5 w-3.5" />
                              ) : (
                                <EyeOff className="h-3.5 w-3.5" />
                              )}
                              {report.is_viewable ? 'Visible' : 'Hidden'}
                            </button>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleDownloadReportForRow(report)}
                                disabled={downloadingReportId === report.id}
                                title="Download PDF report"
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-violet-700 border border-violet-200 hover:bg-violet-50 transition-colors disabled:opacity-50"
                              >
                                {downloadingReportId === report.id ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Download className="h-3.5 w-3.5" />
                                )}
                                PDF
                              </button>
                              <button
                                onClick={() => {
                                  setDeletingReport(report)
                                  setShowDeleteReportModal(true)
                                }}
                                title="Delete this report record"
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Delete run confirmation modal */}
          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={() => deleteRunMutation.mutate(selectedRun.run_name)}
            title="Delete Run"
            message={`This will permanently delete the LARS run "${selectedRun.run_name}" and all stored authority reports for this tenant. This action cannot be undone.`}
            confirmText="Yes, delete"
            cancelText="Cancel"
            type="danger"
            isLoading={deleteRunMutation.isLoading}
          />

          {/* Delete individual report confirmation modal */}
          <ConfirmationModal
            isOpen={showDeleteReportModal}
            onClose={() => {
              setShowDeleteReportModal(false)
              setDeletingReport(null)
            }}
            onConfirm={() => deletingReport && deleteReportMutation.mutate(deletingReport)}
            title="Delete Report"
            message={`This will delete the report record from the database and set is_viewable to false on the authority JSON, preventing it from being re-synced. The generated JSON file is preserved — re-run stage 4 to create a new report.`}
            confirmText="Yes, delete"
            cancelText="Cancel"
            type="danger"
            isLoading={deleteReportMutation.isLoading}
          />
        </div>
      )}
    </div>
  )
}
